import React, { useState, useEffect } from 'react'

import pricingBunny from '~/images/bunnynet-pricing.svg'
import PartialCTA from '../partials/cta'
import { isOverInquiryLimit } from '~/utils/helpers'

const DISK_TYPE = {
  Standard: 'STANDARD',
  Edge: 'EDGE',
}

const DISK_UNIT = {
  GB: 'GB',
  TB: 'TB',
}

const PricingEdgeStorage = ({ block, vars }) => {
  const defaultRegions = !!block.default_regions
    ? Number(block.default_regions)
    : 3
  const defaultStorage = !!block.default_storage_amount
    ? Number(block.default_storage_amount)
    : 20
  const defaultStorageUnit = !!block.default_storage_unit
    ? String(block.default_storage_unit)
    : DISK_UNIT.GB
  const defaultDiskType = !!block.default_disk_type
    ? String(block.default_disk_type)
    : DISK_TYPE.Standard
  const showDiskTypeToggle = !!block.show_disk_type_toggle
    ? Boolean(block.show_disk_type_toggle)
    : false

  const maxGB = 1000
  const maxTB = 50 // input[:checked]

  const [selectedStorageUnit, setSelectedStorageUnit] = useState(
    defaultStorageUnit
  )
  const [selectedDiskType, setSelectedDiskType] = useState(defaultDiskType)
  const [maxUnitValue, setMaxUnit] = useState(maxGB)

  const [replicationPoints, setReplicationPoints] = useState(defaultRegions)
  const [storageAmount, setStorageAmount] = useState(defaultStorage)

  const defaultRegionPrice = calculateRegionPrice(defaultRegions)
  const [regionPrice, setRegionPrice] = useState(defaultRegionPrice)

  const defaultTotalPrice = calculateTotalPrice()
  const [totalPrice, setTotalPrice] = useState(defaultTotalPrice)

  const [showContact, setShowContact] = useState(false)

  function calculateRegionPrice(regions) {
    let regionPrice = 0
    for (let i = 1; i <= regions; i++) {
      if (selectedDiskType === DISK_TYPE.Edge) {
        regionPrice = Number(regionPrice) + 0.02
      } else {
        if (i >= 3) {
          regionPrice =
            Number(regionPrice) +
            Number(vars.pricing_edge_storage_additional_gb)
        } else {
          regionPrice =
            Number(regionPrice) + Number(vars.pricing_edge_storage_first_two_gb)
        }
      }
    }

    return Math.round(regionPrice * 1000) / 1000
  }

  function calculateTotalPrice() {
    let newTotalPrice = Number(regionPrice) * Number(storageAmount)

    if (selectedStorageUnit === DISK_UNIT.TB) {
      newTotalPrice *= 1000
    }

    newTotalPrice = Math.round(newTotalPrice * 1000) / 1000

    return newTotalPrice
  }

  const toggleUnit = () => {
    setMaxUnit(selectedStorageUnit === DISK_UNIT.TB ? maxGB : maxTB)
    setSelectedStorageUnit(
      selectedStorageUnit === DISK_UNIT.TB ? DISK_UNIT.GB : DISK_UNIT.TB
    )
  }

  const getMaxReplicationPoints = () =>
    selectedDiskType === DISK_TYPE.Standard ? 9 : 15

  const findMaxRegion = () => {
    // This logic is inverted,
    const regionMax = selectedDiskType === DISK_TYPE.Edge ? 9 : 15
    if (replicationPoints > regionMax) {
      return regionMax
    }
    return replicationPoints
  }

  const toggleDisk = () => {
    setSelectedDiskType(
      selectedDiskType === DISK_TYPE.Edge ? DISK_TYPE.Standard : DISK_TYPE.Edge
    )
    const regionMax = findMaxRegion()
    setReplicationPoints(regionMax)
  }

  useEffect(() => {
    const newRegionPrice = calculateRegionPrice(replicationPoints)
    setRegionPrice(newRegionPrice)
    const newTotalPrice = calculateTotalPrice()
    setTotalPrice(newTotalPrice)
    setShowContact(isOverInquiryLimit(newTotalPrice))
  }, [
    regionPrice,
    totalPrice,
    replicationPoints,
    storageAmount,
    selectedDiskType,
    selectedStorageUnit,
  ])

  return (
    <section className={`block ${block._template} ${block.css_class_name}`}>
      <div className="container">
        <div className="row">
          <div className="col-lg-6">
            <div className="pricing-wr">
              <div className="pricing-header-wr">
                <div className="pricing-header">
                  <input
                    type="checkbox"
                    name="pricing_switch"
                    checked={selectedStorageUnit === DISK_UNIT.TB}
                    onChange={toggleUnit}
                  />
                  <label htmlFor="pricing_switch"></label>
                </div>
                {showDiskTypeToggle && (
                  <div className="toggle-disk-wr">
                    <input
                      type="checkbox"
                      name="pricing_disk"
                      checked={selectedDiskType === DISK_TYPE.Edge}
                      onChange={toggleDisk}
                    />
                    <label htmlFor="pricing_disk"></label>
                  </div>
                )}
              </div>

              <div className="pricing-main">
                <div className="row">
                  <div className="col-12">
                    <div className="card pricing-storage">
                      <div className="top">
                        <span className="title">Storage amount</span>{' '}
                        <span className="size">
                          {selectedStorageUnit === DISK_UNIT.TB &&
                          storageAmount >= maxTB
                            ? maxTB + '+'
                            : storageAmount}{' '}
                          {selectedStorageUnit}
                        </span>
                      </div>
                      <div className="slidecontainer">
                        <input
                          id="range-eu_na"
                          type="range"
                          className="slider"
                          min="0"
                          max={maxUnitValue}
                          value={storageAmount}
                          onChange={event =>
                            setStorageAmount(Number(event.target.value))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="card pricing-regions">
                      <div className="top">
                        <span className="title">Replication points</span>{' '}
                        <span className="size">{replicationPoints}</span>
                      </div>
                      <div className="slidecontainer">
                        <input
                          id="range-as_oc"
                          type="range"
                          className="slider"
                          min={1}
                          max={getMaxReplicationPoints()}
                          value={replicationPoints}
                          onChange={event =>
                            setReplicationPoints(Number(event.target.value))
                          }
                        />
                        <ul className="replication-points">
                          {
                            <ReplicationPoints
                              points={getMaxReplicationPoints()}
                            />
                          }
                        </ul>
                      </div>
                      <div className="price-region">
                        <span className="text">Price</span>
                        <span className="arrow"></span>
                        <span className="pricing-region">${regionPrice}</span>
                        <span className="unit">/ Gigabyte</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pricing-footer">
                <div className="total">
                  <span className="text">Total</span>
                  <span className="arrow"></span>
                  {showContact ? (
                    <span className="pricing-total">Send inquiry</span>
                  ) : (
                    <>
                      <span className="pricing-total">${totalPrice}</span>
                      <span className="month">/month*</span>
                    </>
                  )}
                </div>
                <div className="cta-wr">
                  {showContact ? (
                    <PartialCTA data={block.cta_contact} />
                  ) : (
                    <PartialCTA data={block.cta_field} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <img
          src={pricingBunny}
          alt="Edge Storage Pricing - bunny.net"
          title="Edge Storage Pricing - bunny.net"
        />
      </div>
    </section>
  )
}

const ReplicationPoints = ({ points }) => {
  const replicationElements = []
  for (let i = 1; i <= points; i++) {
    replicationElements.push(
      <li key={i}>
        <span>{i}</span>
      </li>
    )
  }
  return <>{replicationElements}</>
}

export default PricingEdgeStorage
